'use client'

import { useState, MouseEvent } from 'react'
import { ArrowLeft24, Dots24 } from '@vinted/monochrome-icons'
import { Button, Cell, Divider, Icon, Image, Navigation, Radio, Spacer, Text } from '@vinted/web-ui'
import { produce } from 'immer'
import { compact } from 'lodash'

import { ROOT_CATALOG_ID, WEB_CATALOG_ROOT_ALL_CODE } from '@marketplace-web/catalog/catalog-data'
import {
  getCatalogPhotoSrc,
  WardrobeCatalogWithParentIdModel,
} from '@marketplace-web/profile/profile-data'
import { useTranslate } from '@marketplace-web/i18n/i18n-feature'
import { ScrollableArea } from '@marketplace-web/common-components/scrollable-area-ui'
import { List } from '@marketplace-web/common-components/list-ui'

import FilterDropdown from '../FilterDropdown'

const DROPDOWN_MIN_WIDTH = 300
const SELECT_DROPDOWN_MAX_HEIGHT = 320
const ROOT_CATALOG_ALL_CODE = '_ALL_ROOT' as const

type CatalogItem = {
  id: number
  title: string
  data: Pick<
    WardrobeCatalogWithParentIdModel,
    'id' | 'catalogIds' | 'parentId' | 'code' | 'depth' | 'itemCount' | 'photo'
  >
}

type Props = {
  selectedId: null | number
  catalogs: Array<WardrobeCatalogWithParentIdModel>
  toggleCatalogFilterOption: (item: number | null) => void
  onDropdownOpen?: () => void
}

const CatalogFilter = ({
  selectedId,
  catalogs,
  toggleCatalogFilterOption,
  onDropdownOpen,
}: Props) => {
  const [navigatedCatalogId, setNavigatedCatalogId] = useState<number>(ROOT_CATALOG_ID)

  const translate = useTranslate('user.items.filters.catalog')

  const transformCatalogModelToCatalogItem = (
    catalog: WardrobeCatalogWithParentIdModel,
  ): CatalogItem => {
    const { id, title, catalogIds, parentId, code, depth, itemCount, photo } = catalog

    return {
      id,
      title,
      data: {
        id,
        catalogIds,
        parentId,
        code,
        depth,
        itemCount,
        photo,
      },
    }
  }

  const getCatalogList = (parentId: number) => {
    const parentCatalog = produce(
      catalogs.find(catalog => catalog.id === parentId),
      draft => {
        if (!draft) return

        draft.title = translate('all')
        draft.code = WEB_CATALOG_ROOT_ALL_CODE

        // 'All' catalog is placed alongside its children in the list.
        // Therefore, it must match children's depth.
        draft.depth += 1
      },
    )

    return compact([parentCatalog, ...catalogs.filter(catalog => catalog.parentId === parentId)])
  }

  const getCatalogListItems = (parentId: number) => {
    const catalogList = getCatalogList(parentId)
    const catalogListItems = catalogList.map(transformCatalogModelToCatalogItem)

    if (parentId === ROOT_CATALOG_ID) {
      catalogListItems.unshift({
        id: ROOT_CATALOG_ID,
        title: translate('all'),
        data: {
          id: ROOT_CATALOG_ID,
          catalogIds: [],
          parentId: ROOT_CATALOG_ID,
          code: ROOT_CATALOG_ALL_CODE,
          depth: 0,
          itemCount: catalogList.reduce((acc, { itemCount }) => acc + itemCount, 0),
          photo: null,
        },
      })
    }

    return catalogListItems
  }

  const handleCatalogClick = (item: CatalogItem) => (event: MouseEvent) => {
    event.preventDefault()

    const { catalogIds, code } = item.data

    if (catalogIds.length > 0 && code !== WEB_CATALOG_ROOT_ALL_CODE) {
      setNavigatedCatalogId(Number(item.id))

      return
    }

    if (code === ROOT_CATALOG_ALL_CODE) {
      toggleCatalogFilterOption(null)

      return
    }

    if (item.id) {
      toggleCatalogFilterOption(Number(item.id))
    }
  }

  const handleBackClick = (catalog: WardrobeCatalogWithParentIdModel) => () => {
    setNavigatedCatalogId(Number(catalog.parentId))
  }

  const handleDropdownOpen = () => {
    onDropdownOpen?.()
  }

  const handleRadioClick = (event: MouseEvent<HTMLInputElement>) => {
    event.stopPropagation()
  }

  const getSelectedName = () => {
    if (!selectedId) return translate('all')

    const selectedCatalog = catalogs.find(({ id }) => id === selectedId)

    return selectedCatalog ? selectedCatalog.title : ''
  }

  const renderIcon = (
    photo: WardrobeCatalogWithParentIdModel['photo'],
    code: string,
    depth: number,
  ) => {
    if (depth > 1) return null

    if (code === WEB_CATALOG_ROOT_ALL_CODE || code === ROOT_CATALOG_ALL_CODE) {
      return <Icon name={Dots24} color="primary-default" />
    }

    if (!photo) return null

    return <Image {...getCatalogPhotoSrc(photo)} size="regular" />
  }

  const renderNavigation = () => {
    const catalog = catalogs.find(({ id }) => id === navigatedCatalogId)

    if (!catalog) return null

    return (
      <>
        <Navigation
          left={
            <Button
              styling="flat"
              theme="amplified"
              icon={<Icon name={ArrowLeft24} />}
              onClick={handleBackClick(catalog)}
              testId="catalog-filter-go-back"
            />
          }
          body={catalog.title}
        />
        <Divider />
      </>
    )
  }

  const renderDropdownItem = (item: CatalogItem) => {
    const { catalogIds, code, depth, photo, itemCount } = item.data

    const isRootCatalog = !catalogIds.length || code === WEB_CATALOG_ROOT_ALL_CODE
    const isChecked = selectedId === item.id || (item.id === ROOT_CATALOG_ID && selectedId === null)

    const prefix = renderIcon(photo, code, depth)
    const suffix = isRootCatalog ? (
      <div className="u-flexbox u-align-items-center">
        {itemCount}
        <Spacer orientation="vertical" />
        <Radio
          name="catalog_ids"
          value={item.id}
          aria={{ 'aria-labelledby': `catalog_ids-list-item-${item.id}` }}
          onChange={() => undefined}
          onClick={handleRadioClick}
          checked={isChecked}
          testId={`catalog_ids-list-item-${item.id}-radio`}
        />
      </div>
    ) : (
      itemCount
    )

    return (
      <Cell
        key={item.id}
        id={`catalog_ids-list-item-${item.id}`}
        type="navigating"
        body={<Text text={item.title} type="title" as="span" />}
        prefix={prefix}
        suffix={suffix}
        chevron={!isRootCatalog}
        onClick={handleCatalogClick(item)}
        testId={`catalog_ids-list-item-${item.id}-${isRootCatalog ? 'radio-suffix' : 'chevron-suffix'}`}
      />
    )
  }

  const renderDropdown = () => {
    const items = getCatalogListItems(navigatedCatalogId)

    return (
      <div className="u-ui-padding-vertical-x-small">
        {renderNavigation()}
        <ScrollableArea maxHeight={SELECT_DROPDOWN_MAX_HEIGHT}>
          <List>{items.map(renderDropdownItem)}</List>
        </ScrollableArea>
      </div>
    )
  }

  return (
    <div className="u-flexbox u-align-items-center">
      <Text as="h3" theme="amplified" type="subtitle" text={translate('name')} />
      <Spacer orientation="vertical" />
      <FilterDropdown
        title={getSelectedName()}
        minWidth={DROPDOWN_MIN_WIDTH}
        placement="bottom-end"
        onOpen={handleDropdownOpen}
        testId="catalog-filter-dropdown"
      >
        {renderDropdown()}
      </FilterDropdown>
    </div>
  )
}

export default CatalogFilter
